import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { IconButton } from '@mui/material';
import {
  KeyboardArrowLeftRounded as KeyboardArrowLeftRoundedIcon,
  KeyboardArrowRightRounded as KeyboardArrowRightRoundedIcon,
} from '@mui/icons-material';
import useHorizontalOverflow from '@src/hooks/useHorizontalOverflow';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import useProjectLimit from '@src/hooks/useProjectLimit';
import { useCreateProjectFromInput } from '@src/hooks/useCreateProjectFromInput';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import ROUTES from '@src/constants/route';
import { TYPE_UPLOAD } from '@src/constants/upload';
import { PROJECT_PRODUCT } from '@src/constants/project';
import { MOE_EVENTS, MOE_PROJECT_SOURCE_TYPE } from '@src/constants/moengage';
import { trackEvent } from '@src/services/moengage';
import {
  buildDefaultTitle,
  buildEmptyTtsBlocks,
} from '@src/services/projectInput';
import actions from '@src/redux/actions';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import UploadProjectFilePopup from '@src/containers/Project/ProjectInput/UploadProjectFilePopup';
import ImportProjectUrlPopup from '@src/containers/Project/ProjectInput/ImportProjectUrlPopup';
import { COLOR } from '@src/styles/color';
import glowingStar from '@src/assets/icons/glowing-star.svg';
import CrownImg from '@src/assets/images/crown.png';
import createAudioImage from '@src/assets/images/create-audio-for-lecture-feature.webp';
import lectureDubbingImage from '@src/assets/images/lecture-dubbing-feature.webp';
import textToSpeechImage from '@src/assets/images/text-to-speech-feature.webp';
import videoDubbingImage from '@src/assets/images/video-dubbing-feature.webp';
import voiceCloningImage from '@src/assets/images/voice-cloning-feature.webp';
import FeatureCard from './FeatureCard';
import CreateAudioMenu from './CreateAudioMenu';
import useFeaturePins from './useFeaturePins';
import { SCROLL_STEP, StyledEducatorsHomeFeatures } from './index.style';

// Section heading is BODY/24/SB in the design. The shared scale has no 24px
// semibold, so the bold variant is toned down here rather than widening the
// scale for one heading.
const HEADING_STYLE = { letterSpacing: 0, fontWeight: 600 };

// What the add button does. NAVIGATE goes straight to a tool page; UPLOAD opens
// the shared file popup on a given variant; MENU opens the three-option popover.
const ACTION = {
  NAVIGATE: 'navigate',
  UPLOAD: 'upload',
  MENU: 'menu',
};

// Order matches the design, left to right. "Phụ đề bài giảng" is left out until
// its artwork exists.
//
// `flags` mirrors the gating on the projects page: a tool the account cannot use
// is dropped from the row rather than shown disabled. The two cards that only
// navigate need no flag — their destination pages do their own gating.
const FEATURES = [
  {
    key: 'textToSpeech',
    image: textToSpeechImage,
    titleKey: 'educatorsFeatureTextToSpeechTitle',
    descriptionKey: 'educatorsFeatureTextToSpeechDescription',
    action: { kind: ACTION.NAVIGATE, path: ROUTES.TTS },
  },
  {
    key: 'lectureDubbing',
    image: lectureDubbingImage,
    titleKey: 'educatorsFeatureLectureDubbingTitle',
    descriptionKey: 'educatorsFeatureLectureDubbingDescription',
    action: { kind: ACTION.UPLOAD, variant: TYPE_UPLOAD.PPTX },
    flags: [FEATURE_KEYS.NEW_UI_PROJECTS, FEATURE_KEYS.PRESENTATION_PROJECTS],
  },
  {
    key: 'videoDubbing',
    image: videoDubbingImage,
    titleKey: 'educatorsFeatureVideoDubbingTitle',
    descriptionKey: 'educatorsFeatureVideoDubbingDescription',
    action: { kind: ACTION.UPLOAD, variant: TYPE_UPLOAD.VIDEO_DUBBING },
    flags: [FEATURE_KEYS.NEW_UI_PROJECTS, FEATURE_KEYS.VIDEO_DUBBING_PROJECT],
  },
  {
    key: 'voiceCloning',
    image: voiceCloningImage,
    titleKey: 'educatorsFeatureVoiceCloningTitle',
    descriptionKey: 'educatorsFeatureVoiceCloningDescription',
    action: { kind: ACTION.NAVIGATE, path: ROUTES.VOICE_CLONING_VOICES },
  },
  {
    key: 'createAudio',
    image: createAudioImage,
    titleKey: 'educatorsFeatureCreateAudioTitle',
    descriptionKey: 'educatorsFeatureCreateAudioDescription',
    action: { kind: ACTION.MENU },
    flags: [FEATURE_KEYS.NEW_UI_PROJECTS],
  },
];

const Features = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { user } = useSelector((state) => state.auth);
  const { getFeatureValue } = useFeatureFlags();
  const { checkProjectLimit } = useProjectLimit();
  const { createProject, finalizeProjectDefault } = useCreateProjectFromInput();

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuFeatureKey, setMenuFeatureKey] = useState(null);
  const [openFilePopup, setOpenFilePopup] = useState(false);
  const [filePopupVariant, setFilePopupVariant] = useState(null);
  const [openUrlPopup, setOpenUrlPopup] = useState(false);
  const [projectLimit, setProjectLimit] = useState(0);
  const [openLimitDialog, setOpenLimitDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const flagContext = {
    userId: user?.id,
    email: user?.email,
    phoneNumber: user?.phoneNumber,
  };
  const newUiProjectsEnabled = getFeatureValue(
    FEATURE_KEYS.NEW_UI_PROJECTS,
    flagContext,
  );
  const features = FEATURES.filter(({ flags }) =>
    (flags || []).every((flag) => getFeatureValue(flag, flagContext)),
  );

  const {
    ref: scrollRef,
    canScrollLeft,
    canScrollRight,
    scrollByStep,
  } = useHorizontalOverflow([features.length], SCROLL_STEP);

  const { pinnedKeys, togglePin } = useFeaturePins(
    features.map((feature) => feature.key),
  );

  // Pinned cards lead the row, newest pin first; everything else keeps the order
  // FEATURES declares. Unpinning therefore drops a card back into its default
  // slot on its own, with no previous position to remember.
  const pinnedFeatures = pinnedKeys
    .map((key) => features.find((feature) => feature.key === key))
    .filter(Boolean);
  const orderedFeatures = [
    ...pinnedFeatures,
    ...features.filter((feature) => !pinnedKeys.includes(feature.key)),
  ];

  const handleTogglePin = (key) => () => togglePin(key);

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuFeatureKey(null);
  };

  const createEmptyTtsProject = async () => {
    try {
      const { projectId } = await createProject({
        title: buildDefaultTitle(t),
        product: PROJECT_PRODUCT.TTS,
        blocks: buildEmptyTtsBlocks(),
        sourceType: MOE_PROJECT_SOURCE_TYPE.EMPTY_TEXT,
      });
      finalizeProjectDefault({ projectId, product: PROJECT_PRODUCT.TTS });
    } catch {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'createProjectFailed',
        }),
      );
    }
  };

  // Everything below creates a project, so it goes through the package
  // allowance first — same order as the projects page, where the limit dialog
  // replaces the action instead of letting the user pick a file and fail later.
  const runProjectAction = async (type) => {
    if (isCreating) return;
    setIsCreating(true);
    try {
      const { limit, reached } = await checkProjectLimit();
      if (reached) {
        setProjectLimit(limit);
        setOpenLimitDialog(true);
        return;
      }

      switch (type) {
        case 'TEXT':
          await createEmptyTtsProject();
          break;
        case TYPE_UPLOAD.LINK:
          setOpenUrlPopup(true);
          break;
        case TYPE_UPLOAD.FILE:
        case TYPE_UPLOAD.PPTX:
        case TYPE_UPLOAD.VIDEO_DUBBING:
          setFilePopupVariant(type);
          setOpenFilePopup(true);
          break;
        default:
          break;
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleCardAction = (feature) => (event) => {
    const { action } = feature;
    trackEvent(MOE_EVENTS.EDUCATORS_FEATURE_CLICKED, {
      feature_key: feature.key,
      is_pinned: pinnedKeys.includes(feature.key),
    });
    switch (action.kind) {
      // A tool page does its own project handling, so the allowance is not
      // checked here — nothing is created by navigating.
      case ACTION.NAVIGATE:
        history.push(action.path);
        break;
      case ACTION.MENU:
        setMenuAnchor(event.currentTarget);
        setMenuFeatureKey(feature.key);
        break;
      case ACTION.UPLOAD:
        runProjectAction(action.variant);
        break;
      default:
        break;
    }
  };

  const handleSelectMenuOption = (option) => {
    closeMenu();
    runProjectAction(option.type);
  };

  return (
    <StyledEducatorsHomeFeatures>
      <div className="features-header">
        <div className="features-header-icon">
          <img src={glowingStar} alt="" />
        </div>
        <Typography
          variant="body-24-bold"
          color={COLOR.darkGray}
          style={HEADING_STYLE}
        >
          {t('educatorsFeaturesTitle')}
        </Typography>
      </div>

      <div className="features-strip">
        <div className="features-scroll" ref={scrollRef}>
          {orderedFeatures.map((feature) => (
            <FeatureCard
              key={feature.key}
              image={feature.image}
              title={t(feature.titleKey)}
              description={t(feature.descriptionKey)}
              isPinned={pinnedKeys.includes(feature.key)}
              isMenuOpen={menuFeatureKey === feature.key}
              onTogglePin={handleTogglePin(feature.key)}
              onAdd={handleCardAction(feature)}
            />
          ))}
        </div>

        {canScrollLeft && (
          <div className="features-edge left">
            <IconButton
              className="features-arrow"
              onClick={() => scrollByStep(-1)}
              aria-label={t('educatorsFeaturesScrollPrevious')}
            >
              <KeyboardArrowLeftRoundedIcon className="features-arrow-icon" />
            </IconButton>
          </div>
        )}

        {canScrollRight && (
          <div className="features-edge right">
            <IconButton
              className="features-arrow"
              onClick={() => scrollByStep(1)}
              aria-label={t('educatorsFeaturesScrollNext')}
            >
              <KeyboardArrowRightRoundedIcon className="features-arrow-icon" />
            </IconButton>
          </div>
        )}
      </div>

      <CreateAudioMenu
        anchorEl={menuAnchor}
        onClose={closeMenu}
        onSelect={handleSelectMenuOption}
      />

      {/* Every card that can reach these popups is behind the same flag, so
          there is nothing to mount when it is off. */}
      {newUiProjectsEnabled && (
        <UploadProjectFilePopup
          open={openFilePopup}
          onClose={() => setOpenFilePopup(false)}
          variant={filePopupVariant}
        />
      )}

      {newUiProjectsEnabled && (
        <ImportProjectUrlPopup
          open={openUrlPopup}
          onClose={() => setOpenUrlPopup(false)}
        />
      )}

      <ActionDialog
        open={openLimitDialog}
        onClose={() => setOpenLimitDialog(false)}
        title={t('projectLimitReached')}
        styleTitle={{ textAlign: 'center' }}
        contentComponent={
          <Typography
            style={{ textAlign: 'center' }}
            variant="body-16-regular"
            color={COLOR.descriptionGray}
            dangerouslySetInnerHTML={{
              __html: t('projectLimitReachedDescription', {
                number: projectLimit,
              }),
            }}
          />
        }
        fullWidth
        image={CrownImg}
        imageContainerSize="100px"
        width="640px"
        actionComponent={
          <CommonButton
            variant="contained"
            onClick={() => history.push(ROUTES.PAYMENT)}
            textProps={{
              variantText: 'body-14-semibold',
              color: COLOR.darkBlue,
            }}
            customStyle={{
              height: '40px',
              borderRadius: '8px',
              padding: '8px 24px',
              width: '146px',
            }}
          >
            {t('upgradeNow')}
          </CommonButton>
        }
      />
    </StyledEducatorsHomeFeatures>
  );
};

export default Features;
